<template>
<div>
    <Breadcrumbs main="Sales" title="Sales Overview" />

    <div class="container-fluid">

        <div class="card">
            <div class="card-body">
                <div class="row">
                    <div class="col-md-12 mb-4">

                        <!-- Header -->
                        <div class="d-flex flex-wrap mb-3 justify-content-between align-items-center">
                            <div>
                                <h4 class="mb-1">Sales Overview</h4>
                                <small class="text-muted">
                                    Clean operational view across quotation, fulfillment, delivery and collection.
                                </small>
                            </div>
                        </div>

                        <div class="d-flex flex-row flex-wrap stat-wrapper">
                            <div v-for="(stat, index) in stats" :key="index" class="stat-item">
                                <div class="stat-card" :class="stat.class">
                                    <div class="stat-header">
                                        <div class="stat-icon">
                                            <i :class="stat.icon"></i>
                                        </div>

                                        <span class="live-badge">
                                            Live
                                        </span>
                                    </div>

                                    <div class="stat-body">
                                        <small class="stat-label">
                                            {{ stat.label }}
                                        </small>

                                        <h3 class="stat-value">
                                            {{ stat.value }}
                                        </h3>

                                        <small class="stat-sub">
                                            {{ stat.sub }}
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-12">
                        <div class="operational-card">

                            <!-- Header -->
                            <div class="operational-header">
                                <div>
                                    <h5 class="operational-title">
                                        Operational funnel
                                    </h5>
                                </div>

                                <a href="#" class="report-link">
                                    View report
                                </a>
                            </div>

                            <!-- Funnel Steps -->
                            <div class="d-flex flex-row flex-wrap" style="gap: 12px;">
                                <div v-for="(item, index) in funnelSteps" :key="index" class="">
                                    <div class="funnel-box">

                                        <div class="funnel-top">
                                            <div class="funnel-line" :class="item.class"></div>

                                            <i v-if="index !== funnelSteps.length - 1" class="fa fa-arrow-right arrow-icon"></i>
                                        </div>

                                        <div class="funnel-body">
                                            <small class="funnel-label">
                                                {{ item.label }}
                                            </small>

                                            <h2 class="funnel-value">
                                                {{ item.value }}
                                            </h2>
                                        </div>

                                    </div>
                                </div>
                            </div>

                            <!-- Bottom Stats -->
                            <div class="row g-3 mt-3">

                                <div class="col-md-4">
                                    <div class="metric-card">
                                        <small class="metric-title">
                                            CONVERSION RATE
                                        </small>

                                        <h3 class="metric-value">
                                            {{ funnels.conversion_rate }}%
                                        </h3>

                                        <p class="metric-sub">
                                            Quote to order
                                        </p>
                                    </div>
                                </div>

                                <div class="col-md-4">
                                    <div class="metric-card">
                                        <small class="metric-title">
                                            DISPATCH ON TIME
                                        </small>

                                        <h3 class="metric-value">
                                            {{ funnels.dispatch_on_time_rate }}%
                                        </h3>

                                        <p class="metric-sub">
                                            Last 30 days
                                        </p>
                                    </div>
                                </div>

                                <div class="col-md-4">
                                    <div class="metric-card">
                                        <small class="metric-title">
                                            COLLECTION REALIZATION
                                        </small>

                                        <h3 class="metric-value">
                                            {{ funnels.collection_realization_rate }}%
                                        </h3>

                                        <p class="metric-sub">
                                            Against raised invoices
                                        </p>
                                    </div>
                                </div>

                            </div>

                        </div>
                    </div>

                    <div class="col-12 mt-4">
                        <div class="dashboard-grid">
                            <!-- Orders needing action -->
                            <div class="card-box">
                                <div class="mb-4 operational-title">
                                    <h5>Orders needing action</h5>
                                </div>

                                <div v-if="needLoading" class="loading-state">
                                    Loading...
                                </div>

                                <div v-else-if="needError" class="error-state">
                                    Failed to load orders
                                </div>

                                <div v-else>
                                    <b-table :items="formattedNeedingActions" :fields="needActionFields" responsive bordered hover small class="custom-table">
                                        <!-- Order -->
                                        <template #cell(order_id)="data">
                                            <router-link :to="`/sales/order/detail/${data.item.order_id}`">
                                                <span class="link-style">
                                                {{ data.item.order_id }}
                                            </span>

                                            </router-link>
                                           
                                        </template>

                                        <!-- Customer -->
                                        <template #cell(customer)="data">
                                            {{ data.item.customer }}
                                        </template>

                                        <!-- Status -->
                                        <template #cell(action_status)="data">
                                            <span class="status-badge" :class="statusClass(data.item.action_status)">
                                                {{ capitalize(data.item.action_status) }}
                                            </span>
                                        </template>

                                        <!-- Due -->
                                        <template #cell(due_date)="data">
                                            {{ formatDueDate(data.item.due_date) }}
                                        </template>
                                    </b-table>
                                </div>
                            </div>

                            <!-- Collection risk -->
                            <div class="card-box">
                                <div class="mb-4 operational-title">
                                    <h5>Collection risk</h5>
                                </div>

                                <div v-if="riskLoading" class="loading-state">
                                    Loading...
                                </div>

                                <div v-else-if="riskError" class="error-state">
                                    Failed to load collection risks
                                </div>

                                <div v-else class="risk-list">
                                    <div v-for="(risk, index) in risks" :key="index" class="risk-card">
                                        <div class="risk-top">
                                            <div>
                                                <h6 class="customer-name">
                                                    {{ risk.customer.name }}
                                                </h6>

                                                <p class="invoice-no">
                                                    {{ risk.invoice_no }}
                                                </p>
                                            </div>

                                            <span class="risk-badge" :class="riskClass(risk.risk)">
                                                {{ capitalize(risk.risk) }} risk
                                            </span>
                                        </div>

                                        <div class="risk-info">
                                            <div class="info-row">
                                                <span>Outstanding</span>

                                                <strong>
                                                    ৳ {{ formatNumber(risk.outstanding) }}
                                                </strong>
                                            </div>

                                            <div class="info-row">
                                                <span>Aging</span>

                                                <strong>
                                                    {{ risk.aging_days }} days
                                                </strong>
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="!risks.length" class="empty-state">
                                        No collection risks found
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </div>

</div>
</template>

<script>
import {
    mapState
} from 'vuex';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import Multiselect from 'vue-multiselect';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
export default {
    components: {
        'v-select': vSelect,
        Multiselect
    },
    data() {
        return {
            needActionFields: [{
                    key: 'order_id',
                    label: 'Order'
                },
                {
                    key: 'customer',
                    label: 'Customer'
                },
                {
                    key: 'action_status',
                    label: 'Status'
                },
                {
                    key: 'due_date',
                    label: 'Due'
                }
            ]
        }
    },
    computed: {
        ...mapState({
            summary: state => state.sales.sales_summary,
            funnels: state => state.sales.sales_operation_funnel,
            loading: state => state.sales.salesLoading,
            error: state => state.sales.salesError,

            needingAction: state => state.sales.salesOrderNeedingAction,
            needLoading: state => state.sales.orderActionLoading,
            needError: state => state.sales.orderActionError,

            risks: state => state.sales.collectionRisks,
            riskLoading: state => state.sales.collectionRiskLoading,
            riskError: state => state.sales.collectionRiskLoading,

        }),
        stats() {
            return [{
                    label: 'Quotations Awaiting Review',
                    value: this.summary ?.quotations_awaiting_review ?.count ?? 0,
                    sub: `${this.summary?.quotations_awaiting_review?.today ?? 0} today`,
                    icon: 'fa fa-file',
                    class: 'review-card'
                },
                {
                    label: 'Orders Ready for Fulfillment',
                    value: this.summary ?.orders_ready_for_fulfillment ?.count ?? 0,
                    sub: `${this.summary?.orders_ready_for_fulfillment?.urgent ?? 0} urgent`,
                    icon: 'fa fa-cart-plus',
                    class: 'order-card'
                },
                {
                    label: 'Dispatch Delays',
                    value: this.summary ?.dispatch_delays ?.count ?? 0,
                    sub: `${this.summary?.dispatch_delays?.critical ?? 0} critical`,
                    icon: 'fa fa-truck',
                    class: 'delay-card'
                },
                {
                    label: 'Overdue Collections',
                    value: `৳ ${(this.summary?.overdue_collections?.amount ?? 0).toLocaleString()}`,
                    sub: `${this.summary?.overdue_collections?.accounts ?? 0} accounts`,
                    icon: 'fa fa-folder',
                    class: 'collection-card'
                }
            ];
        },

        funnelSteps() {
            return [{
                    label: 'Quotation',
                    value: this.funnels ?.funnel ?.quotation ?? 0,
                    class: 'quotation-bg'
                },
                {
                    label: 'Order',
                    value: this.funnels ?.funnel ?.order ?? 0,
                    class: 'order-bg'
                },
                {
                    label: 'Fulfillment',
                    value: this.funnels ?.funnel ?.fulfillment ?? 0,
                    class: 'fulfillment-bg'
                },
                {
                    label: 'Delivery',
                    value: this.funnels ?.funnel ?.delivery ?? 0,
                    class: 'delivery-bg'
                },
                {
                    label: 'Collection',
                    value: this.funnels ?.funnel ?.collection ?? 0,
                    class: 'collection-bg'
                }
            ];
        },
        formattedNeedingActions() {
            return this.needingAction.map(item => ({
                order_id: item.order_id,
                customer: item.customer ?.name || '-',
                action_status: item.action_status,
                due_date: item.due_date
            }));
        }
    },
    mounted() {
        this.$store.dispatch("sales/fetchSalesSummary")
        this.$store.dispatch("sales/fetchSalesOperationFunnel")

        this.$store.dispatch("sales/fetchSalesOrderNeedingAction", {
            perPage: null,
            currentPage: null,
            count: 5,
        })
        this.$store.dispatch("sales/fetchSalesCollectionRisk", {
            perPage: null,
            currentPage: null,
            count: 5
        })
    },

    methods: {
        capitalize(value) {
            if (!value) return '';

            return value.charAt(0).toUpperCase() + value.slice(1);
        },

        formatNumber(value) {
            return new Intl.NumberFormat().format(value || 0);
        },

        formatDueDate(date) {
            if (!date) return '-';

            const today = new Date();
            const due = new Date(date);

            const todayStr = today.toDateString();
            const dueStr = due.toDateString();

            if (todayStr === dueStr) {
                return 'Today';
            }

            const tomorrow = new Date();
            tomorrow.setDate(today.getDate() + 1);

            if (tomorrow.toDateString() === dueStr) {
                return 'Tomorrow';
            }

            return due.toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit'
            });
        },

        statusClass(status) {
            return {
                ready: status === 'ready',
                partial: status === 'partial',
                blocked: status === 'blocked'
            };
        },

        riskClass(risk) {
            return {
                high: risk === 'high',
                medium: risk === 'medium',
                low: risk === 'low'
            };
        }
    }
}
</script>

<style>
.stat-wrapper {
    gap: 12px;
}

.stat-item {
    flex: 1 1 220px;
    min-width: 0;
}

.stat-card {
    position: relative;
    height: 100%;
    padding: 14px;
    border-radius: 14px;
    background: #fff;
    border: 1px solid #edf1f7;
    overflow: hidden;
    transition: all 0.25s ease;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
    min-width: 200px;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.stat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
}

.stat-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
}

.live-badge {
    padding: 3px 8px;
    border-radius: 20px;
    background: #f8fafc;
    color: #64748b;
    font-size: 10px;
    font-weight: 600;
}

.stat-body {
    display: flex;
    flex-direction: column;
}

.stat-label {
    color: #64748b;
    font-size: 11px;
    margin-bottom: 6px;
    line-height: 1.4;
}

.stat-value {
    margin: 0;
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1;
}

.stat-sub {
    margin-top: 6px;
    color: #94a3b8;
    font-size: 11px;
    font-weight: 500;
}

/* Accent Top Border */
.stat-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 3px;
}

/* Card Variants */
.review-card::before {
    background: #2563eb;
}

.review-card .stat-icon {
    background: rgba(37, 99, 235, 0.12);
    color: #2563eb;
}

.order-card::before {
    background: #059669;
}

.order-card .stat-icon {
    background: rgba(5, 150, 105, 0.12);
    color: #059669;
}

.delay-card::before {
    background: rgba(220, 38, 38, 1);
}

.delay-card .stat-icon {
    background: rgba(220, 38, 38, 0.12);
    color: #dc2626;
}

.collection-card::before {
    background: #9333ea;
}

.collection-card .stat-icon {
    background: rgba(147, 51, 234, 0.12);
    color: #9333ea;
}

/* Sales Funnels */
.operational-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 28px;
    padding: 20px;
}

.operational-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
}

.operational-title {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    color: #0f172a;
}

.report-link {
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    color: #64748b;
}

.report-link:hover {
    color: #0f172a;
}

/* Funnel Box */
.operational-card {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 22px;
    padding: 16px;
}

.operational-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
}

.operational-title {
    margin: 0;
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
}

.report-link {
    text-decoration: none;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
}

/* Funnel Box */

.funnel-box {
    background: #fff;
    border: 1px solid #dbe3ee;
    border-radius: 18px;
    padding: 14px;
    height: 100%;
    transition: 0.25s ease;
    min-width: 150px;
}

.funnel-box:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.05);
}

.funnel-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}

.funnel-line {
    width: 42px;
    height: 8px;
    border-radius: 20px;
}

.arrow-icon {
    font-size: 11px;
    color: #cbd5e1;
}

.funnel-label {
    display: block;
    color: #64748b;
    font-size: 12px;
    margin-bottom: 6px;
}

.funnel-value {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    line-height: 1;
    color: #0f172a;
}

/* Bottom Metrics */

.metric-card {
    background: #fff;
    border: 1px solid #dbe3ee;
    border-radius: 18px;
    padding: 16px;
    height: 100%;
}

.metric-title {
    display: block;
    color: #64748b;
    font-size: 10px;
    letter-spacing: 0.8px;
    margin-bottom: 8px;
}

.metric-value {
    margin: 0;
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
}

.metric-sub {
    margin: 6px 0 0;
    color: #64748b;
    font-size: 12px;
}

/* Colors */

.quotation-bg {
    background: #0f172a;
}

.order-bg {
    background: #334155;
}

.fulfillment-bg {
    background: #64748b;
}

.delivery-bg {
    background: #94a3b8;
}

.collection-bg {
    background: #cbd5e1;
}

.dashboard-grid {
    display: grid;
    grid-template-columns: 1.4fr 1fr;
    gap: 14px;
}

.card-box {
    background: #fff;
    border: 1px solid #e9edf3;
    border-radius: 14px;
    padding: 14px;
}

.card-header {
    margin-bottom: 10px;
}

.card-header h5 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
}

/* Table */
.custom-table {
    margin-bottom: 0;
    font-size: 13px;
}

.custom-table ::v-deep thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 12px;
    font-weight: 600;
    border-color: #edf2f7;
    padding: 10px 12px;
}

.custom-table ::v-deep tbody td {
    padding: 10px 12px;
    border-color: #edf2f7;
    vertical-align: middle;
}

/* Status */
.status-badge,
.risk-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
}

.status-badge.ready {
    background: #dcfce7;
    color: #15803d;
}

.status-badge.partial {
    background: #fef3c7;
    color: #b45309;
}

.status-badge.blocked {
    background: #fee2e2;
    color: #dc2626;
}

.risk-badge.high {
    background: #fee2e2;
    color: #dc2626;
}

.risk-badge.medium {
    background: #fef3c7;
    color: #b45309;
}

.risk-badge.low {
    background: #dcfce7;
    color: #15803d;
}

/* Risk Card */
.risk-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.risk-card {
    border: 1px solid #edf2f7;
    border-radius: 12px;
    padding: 12px;
}

.risk-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 12px;
}

.customer-name {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
}

.invoice-no {
    margin: 2px 0 0;
    font-size: 12px;
    color: #64748b;
}

.risk-info {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.info-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    color: #64748b;
}

.info-row strong {
    color: #0f172a;
    font-weight: 700;
    font-size: 12px;
}

.loading-state,
.error-state,
.empty-state {
    padding: 20px 10px;
    text-align: center;
    font-size: 13px;
    color: #64748b;
}

@media (max-width: 992px) {
    .dashboard-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .funnel-value {
        font-size: 22px;
    }

    .metric-value {
        font-size: 20px;
    }

    .operational-title {
        font-size: 16px;
    }
}

@media screen and (max-width: 425px) {
    .stat-card {
        width: 100%;
    }

}
</style>
